// LapseNotices (Story 10.5, UX-DR16) — the single host for every lapse surface.
//
// Mounted in BOTH authenticated shells (AppShell and the shell-less
// EvaluationStepperShell — gh-460 dual-mount precedent), it switches on the
// derived lapse phase:
//   countdown → dismissible tiered banner (AC1/AC6)
//   expired   → day-zero modal until acknowledged this session, then the
//               permanent lapsed banner (AC3/AC4)
//   none      → renders nothing (healthy, org_seat, null horizon, loading)
// The 30s + focus re-check rides inside useLapseStatus, so a server-side renewal
// releases whichever surface is up without a reload (AC5).

import { useState } from "react";

import { useAuth } from "@/contexts/auth-context";

import { LapseBanner } from "./LapseBanner";
import { LapseModal, readLapseModalAck } from "./LapseModal";
import { useLapseStatus } from "./useLapseStatus";

export function LapseNotices({ className }: { className?: string }) {
  const { user, isLegacyGated } = useAuth();
  // Clinician-only, never legacy-gated — the TopBar SubscriptionPill precedent:
  // admins have no subscription (and must not fetch/poll subscription-state on
  // their routes), and a gated legacy clinician has no live subscription yet —
  // their banner is the legacy-gate banner, not a lapse surface.
  if (user?.role !== "clinician" || isLegacyGated) return null;
  return <ClinicianLapseNotices userId={user.userId} className={className} />;
}

function ClinicianLapseNotices({
  userId,
  className,
}: {
  userId: string;
  className?: string;
}) {
  const status = useLapseStatus();
  // Session ack read once at mount, scoped to the signed-in user (shared
  // workstations): within one session the only way it flips is the modal's own
  // "View records" action, mirrored into state below.
  const [acked, setAcked] = useState<boolean>(() => readLapseModalAck(userId));

  if (status.phase === "countdown") {
    return (
      <LapseBanner
        mode="countdown"
        userId={userId}
        tier={status.tier}
        daysLeft={status.daysLeft}
        validUntil={status.validUntil}
        className={className}
      />
    );
  }

  if (status.phase === "expired") {
    return acked ? (
      <LapseBanner mode="lapsed" className={className} />
    ) : (
      <LapseModal
        userId={userId}
        validUntil={status.validUntil}
        onAcknowledge={() => setAcked(true)}
      />
    );
  }

  return null;
}
